import { useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon, Loader2Icon } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFetch } from '@/hooks/useFecth';
import { persistAuthSession, pickSessionUser } from '@/services/authSession';
import server from '@/services/server';
import { cn } from '@/lib/utils';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';

function clubInitials(name) {
    return String(name || 'C')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase();
}

function ClubLogo({ name, logo, primaryColor, className }) {
    if (logo) {
        return (
            <img
                src={logo}
                alt=""
                className={cn('size-9 shrink-0 rounded-lg object-contain bg-muted', className)}
            />
        );
    }

    return (
        <span
            className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-semibold text-muted-foreground',
                className,
            )}
            style={
                primaryColor
                    ? { backgroundColor: primaryColor, color: '#fff' }
                    : undefined
            }
        >
            {clubInitials(name)}
        </span>
    );
}

/** Team-switcher no header: trigger alto (logo + nome + “Trocar clube”). */
export default function ClubMembershipSwitcher() {
    const { club } = useClub();
    const { setErrors } = useClubErrors();
    const [switching, setSwitching] = useState(false);

    const clubPortalContainer =
        typeof document !== 'undefined' ? document.getElementById('club-root') : null;

    const { data: memberships } = useFetch('club/memberships');
    const clubs = Array.isArray(memberships) ? memberships : [];

    const current =
        clubs.find(item => item.current || Number(item.id) === Number(club?.id)) ||
        (club
            ? { id: club.id, name: club.name, logo: club.logo, primaryColor: club.primaryColor }
            : null);

    const handleSwitch = async targetClubId => {
        if (!targetClubId || Number(targetClubId) === Number(club?.id) || switching) return;

        try {
            setSwitching(true);
            const { data } = await server.post('club/memberships/switch', {
                clubId: Number(targetClubId),
            });
            const payload = data?.data || data;
            persistAuthSession({
                token: payload.token,
                refreshtoken: payload.refreshToken,
                user: pickSessionUser(payload.user),
                club: payload.club,
            });
            server.defaults.headers.Authorization = `Bearer ${payload.token}`;
            window.location.assign('/clube');
        } catch (error) {
            setErrors(error);
            setSwitching(false);
        }
    };

    if (!current && clubs.length === 0) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                disabled={switching}
                render={
                    <button
                        type="button"
                        className={cn(
                            'flex h-11 max-w-[min(280px,55vw)] items-center gap-2.5 rounded-xl border border-border bg-background px-2.5 text-left outline-none transition-colors',
                            'hover:bg-muted/70 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                            'data-popup-open:bg-muted/70 data-popup-open:ring-1 data-popup-open:ring-border',
                            'disabled:pointer-events-none disabled:opacity-60',
                        )}
                    />
                }
            >
                <ClubLogo
                    name={current?.name}
                    logo={current?.logo}
                    primaryColor={current?.primaryColor}
                />
                <div className="grid min-w-0 flex-1 leading-tight">
                    <span className="truncate text-sm font-semibold">{current?.name || 'Clube'}</span>
                    <span className="truncate text-xs text-muted-foreground">
                        {clubs.length > 1 ? 'Trocar clube' : 'Clube atual'}
                    </span>
                </div>
                {switching ? (
                    <Loader2Icon className="size-4 shrink-0 animate-spin opacity-70" />
                ) : (
                    <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                )}
            </DropdownMenuTrigger>

            <DropdownMenuContent
                container={clubPortalContainer}
                className="min-w-64 w-(--anchor-width) rounded-xl p-1.5"
                side="bottom"
                align="end"
                sideOffset={6}
            >
                {clubs.map(item => {
                    const isCurrent = item.current || Number(item.id) === Number(club?.id);
                    return (
                        <DropdownMenuItem
                            key={item.id}
                            disabled={switching}
                            onClick={() => {
                                if (!isCurrent) handleSwitch(item.id);
                            }}
                            className={cn(
                                'gap-2.5 rounded-lg px-2 py-2',
                                isCurrent && 'bg-muted',
                            )}
                        >
                            <ClubLogo
                                name={item.name}
                                logo={item.logo}
                                primaryColor={item.primaryColor}
                            />
                            <span className="min-w-0 flex-1 truncate text-sm font-medium">
                                {item.name}
                            </span>
                            {isCurrent ? (
                                <CheckIcon className="size-4 shrink-0 text-primary" />
                            ) : null}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
